import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { IDropdown, IHotel } from "../../models/BaseModel";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { JVInput } from "../../shared/JVInput";
import { JVCheckbox } from "../../shared/JVCheckbox";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVTextArea } from "../../shared/JVTextarea";
import { JVInputMask } from "../../shared/JVInputMask";
import LocationAutocomplete, { NominatimResponse } from "../../shared/LocationAutoCompleteGM";

const schema = yup.object({
    name: yup.string().required("Hotel Name is required"),
    email: yup.string().required("Email is required").email('Invailid Email'),
    phone: yup.string().required("Phone is required"),
    starRating: yup.number().required("StarRating is required"),
    countryId: yup.number().required("Country is required").min(1, "Country is required"),
    stateId: yup.number().required("State is required").min(1, "State is required"),
    cityId: yup.number().required("City is required").min(1, "City is required"),
    hotelCategoryId: yup.number().required("HotelCategory is required").min(1, "HotelCategory is required"),
    postalCode: yup.string().nullable().notRequired().matches(/^[1-9][0-9]{5}$/, 'Invalid postal code')
});

interface Iprops {
    singleData: IHotel,
    open: boolean,
    hideDialog: () => void,
    toast: any,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

export const Form = (props: Iprops) => {
    const [submitting, setSubmitting] = useState(false);
    const [countries, setCountries] = useState<IDropdown[]>();
    const [states, setStates] = useState<IDropdown[]>();
    const [cities, setCities] = useState<IDropdown[]>();
    const [hotelCategories, setHotelCategories] = useState<IDropdown[]>();
    const [hotelCahins, setHotelChains] = useState<IDropdown[]>();

    const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<IHotel>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData,
        shouldFocusError: false,
    });

    const countryId = watch('countryId');
    const stateId = watch('stateId');
    const lat = watch('latitude');
    const lng = watch('longitude');

    useEffect(() => {
        reset(props.singleData);
    }, [props.singleData]);

    const onLocationSelect = (location?: NominatimResponse) => {
        if (location) {
            setValue('latitude', parseFloat(location.lat));
            setValue('longitude', parseFloat(location.lng));
        } else {
            setValue('latitude', undefined);
            setValue('longitude', undefined);
        }
    }

    const loadCities = async (stateId: number) => {
        const response = await Fetch(`city/touristplaces/${stateId}`, "GET", null, null, null);
        if (response) {
            setCities(await response.json());
        }
    }
    const loadStates = async (countryId: number) => {
        const response = await Fetch(`state/touristplaces/${countryId}`, "GET", null, null, null);
        if (response) {
            setStates(await response.json());
        }
    }
    const loadCountries = async () => {
        const response = await Fetch(`country/touristplaces`, "GET", null, null, null);
        if (response) {
            setCountries(await response.json());
        }
    }
    const loadHotelCategories = async () => {
        const response = await Fetch(`hotelCategory/dropdown`, "GET", null, null, null);
        if (response) {
            setHotelCategories(await response.json());
        }
    }
    const loadHotelChains = async () => {
        const response = await Fetch(`hotelChain/dropdown`, "GET", null, null, null);
        if (response) {
            setHotelChains(await response.json());
        }
    }

    useEffect(() => {
        loadCountries();
        loadHotelCategories();
        loadHotelChains();
    }, []);
    useEffect(() => { if (countryId) loadStates(countryId) }, [countryId]);
    useEffect(() => { if (stateId) loadCities(stateId) }, [stateId]);

    const saveToDB = async (body: IHotel) => {
        setSubmitting(true);
        const method = props.singleData.id ? "PUT" : "POST";
        const message = props.singleData.id ? "Updated Successfully" : "Added Successfully";

        const response = await Fetch(`hotel${props.singleData.id ? '/' + props.singleData.id : ''}`, method, body, null, props.toast);
        if (response) {
            onHide();
            props.toast.current.show({ severity: 'success', detail: message, life: 3000 });
            props.reloadData();
        }
        setSubmitting(false);
    }

    const onHide = () => {
        props.hideDialog();
        reset({});
    }

    function onSubmit(data: any) {
        saveToDB(data);
    }

    return (
        <Dialog visible={props.open} style={{ width: '80vw', maxWidth: '800px' }} header="Add/Edit Hotel" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                <JVInput required label="Hotel Name" control={control} name="name" error={errors.name?.message} />
                <JVInput required label="Email" control={control} name="email" error={errors.email?.message} />
                <JVInputMask required label="Phone" mask="9999999999" control={control} name="phone" error={errors.phone?.message} />
                <JVInput label="Website" control={control} name="website" error={errors.website?.message} />
                <JVDropdown label="Hotel Chain" control={control} options={hotelCahins} optionLabel="name" optionValue="id" name="hotelChainId" error={errors.hotelChainId?.message} />
                <JVDropdown required control={control} label="Hotel Category" options={hotelCategories} optionLabel="name" optionValue="id" name="hotelCategoryId" error={errors.hotelCategoryId?.message} />
                <JVDropdown column={3} required control={control} label="Star Rating" options={[
                    { id: 1, name: '1 Star' },
                    { id: 2, name: '2 Star' },
                    { id: 3, name: '3 Star' },
                    { id: 4, name: '4 Star' },
                    { id: 5, name: '5 Star' }
                ]} optionLabel="name" optionValue="id" name="starRating" error={errors.starRating?.message} />
                {/* <JVInput column={3} type="number" label="Latitude" control={control} name="latitude" error={errors.latitude?.message} />
                <JVInput column={3} type="number" label="Longitude" control={control} name="longitude" error={errors.longitude?.message} /> */}
                <LocationAutocomplete col="8" label={`Hotel Geographic Coordinates (Lat: ${lat ?? ""} | Lng: ${lng ?? ""})`} onLocationSelect={onLocationSelect} countryCode="in" />
                <JVDropdown column={3} required control={control} label="Country" options={countries} optionLabel="name" optionValue="id" name="countryId" error={errors.countryId?.message} />
                <JVDropdown column={3} required control={control} label="State" options={states} optionLabel="name" optionValue="id" name="stateId" error={errors.stateId?.message} />
                <JVDropdown column={3} required control={control} label="City" options={cities} optionLabel="name" optionValue="id" name="cityId" error={errors.cityId?.message} />
                <JVInput column={3} label="Postal Code" control={control} name="postalCode" error={errors.postalCode?.message} />
                <JVInput column={1.5} label="Street Address" control={control} name="address" error={errors.address?.message} />
                {/* <JVMultiselect column={1} label="Amenities" options={amenities} optionLabel="name" placeholder="Select Amenities" value={selectedAmenities} onChange={setSelectedAmenities} /> */}
                <JVTextArea column={1} label="Description" control={control} name="description" error={errors.description?.message} />
                {props.singleData.id && <JVCheckbox column={2} label="Status" control={control} name="status" />}

                <div className="col-12 md:col-12 flex gap-4 justify-content-end mt-2">
                    <Button style={{ width: '100px' }} loading={submitting} label="SAVE" type="submit" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </form>
        </Dialog>
    );
}